import React from 'react';
import useMqtt from './hooks/useMqtt';
import EcgChart from './components/EcgChart';
import VitalCard from './components/VitalCard';
import AlertBanner from './components/AlertBanner';
import AuthStatus from './components/AuthStatus';
import CommandButtons from './components/CommandButtons';
import MotionPanel from './components/MotionPanel';
import PinLock from './auth/PinLock';
import useSession from './auth/useSession';

function hrStatus(hr)   { if (hr == null) return 'ok'; if (hr < 50 || hr > 180) return 'critical'; return 'ok'; }
function spo2Status(v)  { if (v  == null) return 'ok'; if (v < 94) return 'critical'; if (v < 96) return 'warning'; return 'ok'; }
function tempStatus(v)  { if (v  == null) return 'ok'; if (v > 38.5 || v < 35.0) return 'critical'; if (v > 37.5) return 'warning'; return 'ok'; }

export default function App() {
  const { connected, ecgData, hr, spo2, temp, motion, alert, auth } = useMqtt();
  const { unlocked, login, logout, error, loading } = useSession();
  const accel    = motion?.accel;
  const isMotion = motion?.motion === true;

  // Mask vitals when device ECG auth is rejected
  const deviceAuthed = auth.status === 'AUTHENTICATED' || auth.status === 'PENDING';

  // Show lock screen if session not unlocked
  if (!unlocked) {
    return <PinLock onLogin={login} error={error} loading={loading} />;
  }

  return (
    <div style={s.page}>

      {/* ── Header ───────────────────────────────────────── */}
      <header style={s.header}>
        <div style={s.logoRow}>
          <div style={s.logoBadge}>
            <svg width="22" height="22" viewBox="0 0 24 24" fill="none">
              <path d="M3 12h3l2-7 4 14 3-10 2 3h4" stroke="#6366f1" strokeWidth="2.2"
                strokeLinecap="round" strokeLinejoin="round"/>
            </svg>
          </div>
          <div>
            <h1 style={s.h1}>SmartPatch</h1>
            <p style={s.subtitle}>Athlete Biometric Monitor · Athlete&nbsp;01</p>
          </div>
        </div>

        <div style={s.headerRight}>
          <div style={{ ...s.connPill, background: connected ? '#ecfdf5' : '#fff1f2', borderColor: connected ? '#a7f3d0' : '#fecaca' }}>
            <span style={{
              ...s.connDot,
              background: connected ? '#10b981' : '#ef4444',
              animation : connected ? 'pulse-ring 2s infinite' : 'pulse-ring-red 2s infinite',
            }} />
            <span style={{ ...s.connText, color: connected ? '#059669' : '#dc2626' }}>
              {connected ? 'Live' : 'Offline'}
            </span>
          </div>
          <div style={s.timeBadge}>
            {new Date().toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
          </div>
          <button onClick={logout} style={s.logoutBtn} title="Lock dashboard">
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round">
              <rect x="3" y="11" width="18" height="11" rx="2"/>
              <path d="M7 11V7a5 5 0 0 1 10 0v4"/>
            </svg>
            Lock
          </button>
        </div>
      </header>

      {/* ── Alert Banner ─────────────────────────────────── */}
      <AlertBanner level={alert.level} msg={alert.msg || 'All systems nominal — monitoring active'} />

      {/* ── ECG Chart ────────────────────────────────────── */}
      <EcgChart data={ecgData} />

      {/* ── Device auth rejection banner ─────────────────── */}
      {auth.status === 'REJECTED' && (
        <div style={s.rejectedBanner}>
          <span style={s.rejectedIcon}>🔒</span>
          <div>
            <strong style={{ color: '#dc2626' }}>Device Identity Rejected</strong>
            <span style={s.rejectedSub}> — ECG waveform mismatch. Vitals hidden until re-authentication.</span>
          </div>
        </div>
      )}

      {/* ── Vitals Grid ──────────────────────────────────── */}
      <div style={s.vitalsGrid}>
        <VitalCard
          icon="heart"
          label="Heart Rate"
          value={deviceAuthed && hr != null ? Math.round(hr) : null}
          unit="BPM"
          status={hrStatus(hr)}
          sub={!deviceAuthed ? 'Identity unverified' : hr != null && (hr < 50 || hr > 180) ? 'Out of range' : 'Normal sinus'}
          min={40} max={200} normal={[60,100]}
        />
        <VitalCard
          icon="drop"
          label="SpO₂"
          value={deviceAuthed && spo2 != null ? spo2.toFixed(1) : null}
          unit="%"
          status={spo2Status(spo2)}
          sub={!deviceAuthed ? 'Identity unverified' : spo2 != null && spo2 < 94 ? 'Hypoxia' : spo2 != null && spo2 < 96 ? 'Low oxygen' : 'Normal'}
          min={88} max={100} normal={[96,100]}
        />
        <VitalCard
          icon="temp"
          label="Skin Temp"
          value={deviceAuthed && temp != null ? temp.toFixed(1) : null}
          unit="°C"
          status={tempStatus(temp)}
          sub={!deviceAuthed ? 'Identity unverified' : temp != null && temp > 38.5 ? 'Hyperthermia' : temp != null && temp > 37.5 ? 'Elevated' : 'Normal'}
          min={34} max={40} normal={[35.5,37.5]}
        />
        <VitalCard
          icon="motion"
          label="Acceleration"
          value={accel != null ? accel.toFixed(1) : null}
          unit="m/s²"
          status={isMotion ? 'motion' : 'ok'}
          sub={isMotion ? 'High motion detected' : 'Stable'}
          min={0} max={30} normal={[0,15]}
        />
      </div>

      {/* ── IMU Detail ───────────────────────────────────── */}
      {motion && <MotionPanel motion={motion} />}

      {/* ── Bottom Row ───────────────────────────────────── */}
      <div style={s.bottomRow}>
        <AuthStatus auth={auth} />
        <div style={s.cmdCard}>
          <div style={s.cmdHeader}>
            <span style={s.cmdIcon}>⚡</span>
            <span style={s.cmdTitle}>Remote Commands</span>
          </div>
          <CommandButtons />
        </div>
      </div>

      {/* ── Footer ───────────────────────────────────────── */}
      <footer style={s.footer}>
        SmartPatch v1.0 · STM32F103 + ESP8266 · HiveMQ MQTT · Cloudflare Pages
      </footer>
    </div>
  );
}

const s = {
  page: {
    maxWidth : 960,
    margin   : '0 auto',
    padding  : '28px 20px 48px',
    display  : 'flex',
    flexDirection: 'column',
    gap      : 18,
    animation: 'fade-in .4s ease',
  },

  /* Header */
  header: {
    display       : 'flex',
    alignItems    : 'center',
    justifyContent: 'space-between',
    padding       : '18px 24px',
    background    : 'rgba(255,255,255,0.8)',
    backdropFilter: 'blur(20px)',
    WebkitBackdropFilter: 'blur(20px)',
    borderRadius  : 20,
    border        : '1px solid rgba(255,255,255,0.9)',
    boxShadow     : '0 4px 24px rgba(99,102,241,0.08), 0 1px 0 rgba(255,255,255,0.9) inset',
  },
  logoRow : { display: 'flex', alignItems: 'center', gap: 14 },
  logoBadge: {
    width       : 44,
    height      : 44,
    borderRadius: 12,
    background  : 'linear-gradient(135deg, #eef2ff, #e0e7ff)',
    border      : '1px solid #c7d2fe',
    display     : 'flex',
    alignItems  : 'center',
    justifyContent: 'center',
    flexShrink  : 0,
  },
  h1      : { fontSize: 20, fontWeight: 800, color: '#1e1b4b', letterSpacing: '-0.3px' },
  subtitle: { fontSize: 12, color: '#6b7280', marginTop: 1, fontWeight: 500 },
  headerRight: { display: 'flex', alignItems: 'center', gap: 10 },
  connPill: {
    display     : 'flex',
    alignItems  : 'center',
    gap         : 7,
    padding     : '6px 14px',
    borderRadius: 99,
    border      : '1px solid',
    fontSize    : 13,
    fontWeight  : 600,
  },
  connDot : { width: 8, height: 8, borderRadius: '50%', flexShrink: 0 },
  connText: { fontSize: 13, fontWeight: 600 },
  timeBadge: {
    fontSize    : 13,
    fontWeight  : 600,
    color       : '#6b7280',
    padding     : '6px 12px',
    background  : 'rgba(243,244,246,0.8)',
    borderRadius: 99,
    border      : '1px solid #e5e7eb',
  },

  /* Vitals */
  vitalsGrid: {
    display            : 'grid',
    gridTemplateColumns: 'repeat(auto-fit, minmax(190px, 1fr))',
    gap                : 14,
  },

  /* Bottom row */
  bottomRow: { display: 'flex', gap: 14, flexWrap: 'wrap', alignItems: 'stretch' },
  cmdCard  : {
    flex            : 1,
    minWidth        : 260,
    background      : 'rgba(255,255,255,0.72)',
    backdropFilter  : 'blur(20px)',
    WebkitBackdropFilter: 'blur(20px)',
    borderRadius    : 20,
    border          : '1px solid rgba(255,255,255,0.9)',
    boxShadow       : '0 8px 32px rgba(99,102,241,0.08), 0 1px 0 rgba(255,255,255,0.9) inset',
    padding         : '20px 22px',
    display         : 'flex',
    flexDirection   : 'column',
    gap             : 14,
  },
  cmdHeader: { display: 'flex', alignItems: 'center', gap: 8 },
  cmdIcon  : { fontSize: 17 },
  cmdTitle : { fontSize: 13, fontWeight: 700, color: '#374151', textTransform: 'uppercase', letterSpacing: '0.06em' },

  footer: {
    textAlign : 'center',
    fontSize  : 11,
    color     : '#9ca3af',
    marginTop : 4,
    fontWeight: 500,
  },

  logoutBtn: {
    display     : 'flex',
    alignItems  : 'center',
    gap         : 6,
    padding     : '6px 14px',
    borderRadius: 99,
    border      : '1px solid #e5e7eb',
    background  : 'rgba(243,244,246,0.8)',
    color       : '#6b7280',
    fontSize    : 13,
    fontWeight  : 600,
    fontFamily  : 'Inter, sans-serif',
    cursor      : 'pointer',
    transition  : 'all .15s ease',
  },

  rejectedBanner: {
    display     : 'flex',
    alignItems  : 'center',
    gap         : 12,
    padding     : '12px 18px',
    background  : '#fff1f2',
    border      : '1px solid #fecaca',
    borderRadius: 14,
    fontSize    : 13,
    color       : '#374151',
    animation   : 'slide-in .3s ease',
  },
  rejectedIcon: { fontSize: 18, flexShrink: 0 },
  rejectedSub : { color: '#6b7280', fontWeight: 500 },
};
